import { EventMeta } from '../../../interfaces/meta.interface';
import { ForwardSubject, getMailForwardClx, getParam } from './New-InboxRule';

/**
 * What `Set-Mailbox` records of a mailbox's own forward: `ForwardingSmtpAddress` (an outside address, logged as
 * `smtp:a@b.com`) and `ForwardingAddress` (a directory recipient). Shaped like the inbox-rule fields so that the
 * forwarding classifications and `isForwardedOutsideOfOrg` read it unchanged: both addresses go in `forward_to`,
 * joined by `;` as the audit log joins a rule's recipients.
 */
type SetMailboxFields = {
    /** Whether this change set a forwarding address. A cmdlet that clears one, or changes something else, is false. */
    forwarding_set: boolean;
    forward_to: string | undefined;
    org: string | undefined;
};

/** A mailbox's own forward, as the forwarding classifications describe it. */
const MAILBOX_FORWARD: ForwardSubject = {
    a: 'mailbox forwarding',
    forwarding: 'mailbox forwarding',
    Forwarding: 'Mailbox forwarding',
    was: (action) => `Forwarding was ${action} on a mailbox`,
};

/**
 * `Set-Mailbox`: the cmdlet behind the OWA / admin-centre "forward my email" setting, and many other mailbox changes.
 * Until OS-475 (2026-10-30) no handler existed, so a forward to an outside address set this way was never an alert:
 * Clarity's Mail Flow graded it Critical in the browser only, and the alert e-mail had nothing to send. A change that
 * sets a forwarding address now takes the inbox-rule forwarding grades (Danger outside the organisation from an
 * approved location, Critical/Danger by the actor's reputation and location); every other `Set-Mailbox` is Info.
 */
const SetMailbox: EventMeta<SetMailboxFields> = {
    title: 'Mailbox Updated',
    default: {
        description: 'Mailbox settings were updated',
        longDescription: "A mailbox's settings were changed. No forwarding address was set by this change.",
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        const forward_to =
            [getParam(log, 'ForwardingSmtpAddress'), getParam(log, 'ForwardingAddress')]
                .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
                .join(';') || undefined;
        return {
            forwarding_set: forward_to !== undefined,
            forward_to,
            org: log.OrganizationName,
        };
    },
    classifications: [
        ...getMailForwardClx('set', MAILBOX_FORWARD),
        // Every other Set-Mailbox: the operation's own Info, since an unmatched event falls to the generic default.
        {
            severity: 'info',
            description: () => 'Mailbox settings were updated',
            longDescription: () => "A mailbox's settings were changed. No forwarding address was set by this change.",
        },
    ],
};

export default SetMailbox;
